import { useRef, type TouchEvent } from 'react';

export interface UseSwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  /** Minimum horizontal travel, in px, before a swipe fires. Default 60. */
  threshold?: number;
  /**
   * How much more horizontal than vertical the gesture must be to count as a swipe.
   * Default 1.5 — i.e. |dx| must exceed 1.5 × |dy|.
   */
  directionRatio?: number;
}

export interface SwipeHandlers {
  onTouchStart: (e: TouchEvent) => void;
  onTouchMove: (e: TouchEvent) => void;
  onTouchEnd: (e: TouchEvent) => void;
}

/**
 * Horizontal swipe detection for a scrollable list body.
 *
 * Returns touch handlers to spread onto the element you want swipeable:
 *
 * @example
 * const swipe = useSwipe({ onSwipeLeft: nextDay, onSwipeRight: prevDay });
 * <div {...swipe}>…</div>
 *
 * The gesture is *rejected* the moment vertical travel dominates — once a drag looks
 * like a scroll, it stays a scroll for the rest of the touch, even if the finger later
 * curves sideways. Without that lock the hook fights the list's own scrolling and days
 * flip while the user is just reading. Multi-touch (pinch-zoom) is ignored too.
 *
 * Nothing here calls `preventDefault`, so native scrolling and momentum are untouched.
 */
export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = 60,
  directionRatio = 1.5,
}: UseSwipeOptions): SwipeHandlers {
  const startX = useRef(0);
  const startY = useRef(0);
  // null = no active gesture; false = disqualified (vertical scroll or multi-touch).
  const tracking = useRef<boolean | null>(null);

  function onTouchStart(e: TouchEvent) {
    if (e.touches.length !== 1) {
      tracking.current = false;
      return;
    }
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    tracking.current = true;
  }

  function onTouchMove(e: TouchEvent) {
    if (!tracking.current) return;
    if (e.touches.length !== 1) {
      tracking.current = false;
      return;
    }
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    // Wait for the gesture to declare itself before judging it; the first few pixels of
    // any touch are noise.
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
    if (Math.abs(dy) >= Math.abs(dx)) tracking.current = false;
  }

  function onTouchEnd(e: TouchEvent) {
    const wasTracking = tracking.current;
    tracking.current = null;
    if (!wasTracking) return;

    const touch = e.changedTouches[0];
    if (!touch) return;

    const dx = touch.clientX - startX.current;
    const dy = touch.clientY - startY.current;
    if (Math.abs(dx) < threshold) return;
    if (Math.abs(dx) < Math.abs(dy) * directionRatio) return;

    if (dx < 0) onSwipeLeft?.();
    else onSwipeRight?.();
  }

  return { onTouchStart, onTouchMove, onTouchEnd };
}
